import React, { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, MapPin, Truck, Flag, Clock, Route as RouteIcon, LocateFixed, Loader2, Radio, Square } from "lucide-react";

// Fix leaflet's default-icon paths (webpack strips them by default).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Coloured circular markers to distinguish pickup vs dropoff.
const coloredIcon = (color, letter) => L.divIcon({
  className: "",
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -12],
  html: `<div style="
    width:30px;height:30px;border-radius:50%;background:${color};
    color:#fff;font-family:ui-sans-serif,system-ui;font-weight:800;font-size:14px;
    display:flex;align-items:center;justify-content:center;
    box-shadow:0 2px 6px rgba(0,0,0,0.35);border:2px solid #fff;">${letter}</div>`,
});

const PICKUP_ICON = coloredIcon("#F97316", "P");   // orange
const DROPOFF_ICON = coloredIcon("#059669", "D"); // green
const YOU_ARE_HERE_ICON = coloredIcon("#2563EB", "•"); // blue dot

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
    } else {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
    }
  }, [map, points]);
  return null;
}

/**
 * iOS Safari fix — Leaflet initializes with 0px height on mobile stacked layouts.
 * We force `invalidateSize()` after mount + window resize so tiles paint
 * once the container has real dimensions.
 */
function InvalidateSizeOnMount() {
  const map = useMap();
  useEffect(() => {
    const tick = () => { try { map.invalidateSize(); } catch {} };
    const timers = [50, 200, 500, 1000, 2000].map((t) => setTimeout(tick, t));
    window.addEventListener("resize", tick);
    window.addEventListener("orientationchange", tick);
    document.addEventListener("visibilitychange", tick);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", tick);
      window.removeEventListener("orientationchange", tick);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [map]);
  return null;
}

const STATUS_COLOR = {
  scheduled: "#9CA3AF",
  picked_up: "#F59E0B",
  in_transit: "#F97316",
  delivered: "#059669",
  cancelled: "#6B7280",
  rejected: "#DC2626",
};

/**
 * Slide-in map drawer showing a single load's route.
 *
 * Pickup and dropoff are pulled from the load's coords first, falling back to
 * the site records. If neither is available we show a friendly empty state.
 */
export default function LoadMapDrawer({ load, sites, onClose }) {
  const [travel, setTravel] = useState(null);
  const [loadingTravel, setLoadingTravel] = useState(false);
  const [locating, setLocating] = useState(false);
  const [myPos, setMyPos] = useState(null); // {lat, lon, accuracy_m, distance_mi, near_dropoff}
  const [breadcrumbs, setBreadcrumbs] = useState([]); // ordered [{lat, lon, at, ...}]
  const [tracking, setTracking] = useState(false);
  const trackTimerRef = useRef(null);

  const isTerminal = ["delivered", "cancelled", "rejected"].includes(load?.status);

  // Load breadcrumb history when the drawer opens.
  const refreshHistory = useCallback(async () => {
    if (!load?.id) return;
    try {
      const { data } = await api.get(`/loads/${load.id}/location-history`);
      setBreadcrumbs(data || []);
    } catch { /* silent */ }
  }, [load?.id]);
  useEffect(() => { refreshHistory(); }, [refreshHistory]);

  const _sendPing = async (lat, lon, accuracy, label) => {
    try {
      const { data } = await api.post(`/loads/${load.id}/location`, {
        lat, lon, accuracy_m: accuracy || null, label,
      });
      setMyPos({
        lat, lon,
        accuracy_m: accuracy || null,
        distance_mi: data.distance_to_dropoff_mi,
        near_dropoff: data.near_dropoff,
      });
      refreshHistory();
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to log location");
      throw e;
    }
  };

  const locate = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Your browser does not support geolocation.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const data = await _sendPing(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy, "locate-me");
          if (data.near_dropoff) {
            toast.success(`At dropoff (within ${Math.round((data.distance_to_dropoff_mi || 0) * 5280)} ft) — logged`);
          } else if (data.distance_to_dropoff_mi != null) {
            toast.success(`Location logged · ${data.distance_to_dropoff_mi.toFixed(2)} mi from dropoff`);
          } else {
            toast.success("Location logged");
          }
        } catch { /* toast already fired */ }
        finally { setLocating(false); }
      },
      (err) => { toast.error(`Location error: ${err.message}`); setLocating(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  // "Track my drive" — pings every 60s until stopped or load reaches a terminal state.
  const startTracking = () => {
    if (!("geolocation" in navigator)) { toast.error("Geolocation unsupported."); return; }
    if (isTerminal) { toast.error("Load already closed — cannot track."); return; }
    setTracking(true);
    toast.success("Tracking on · will ping every 60s");
    const ping = () => navigator.geolocation.getCurrentPosition(
      (pos) => _sendPing(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy, "auto-track")
        .then((data) => {
          if (data?.near_dropoff) {
            toast.success("You're at the dropoff. Consider marking the load delivered.");
          }
        })
        .catch(() => {}),
      (err) => toast.error(`GPS error: ${err.message}`),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    );
    ping();  // immediate ping so trail starts right away
    trackTimerRef.current = setInterval(ping, 60000);
  };
  const stopTracking = useCallback(() => {
    if (trackTimerRef.current) clearInterval(trackTimerRef.current);
    trackTimerRef.current = null;
    setTracking(false);
  }, []);
  useEffect(() => () => stopTracking(), [stopTracking]); // cleanup on unmount

  const siteById = React.useMemo(() => {
    const m = {};
    (sites || []).forEach((s) => { m[s.id] = s; });
    return m;
  }, [sites]);
  const pickupSite = siteById[load?.pickup_site_id] || {};
  const dropoffSite = siteById[load?.dropoff_site_id] || {};

  const pLat = pickupSite.lat ?? load?.pickup_lat;
  const pLon = pickupSite.lon ?? load?.pickup_lon;
  const dLat = dropoffSite.lat ?? load?.dropoff_lat;
  const dLon = dropoffSite.lon ?? load?.dropoff_lon;

  const points = [];
  if (pLat != null && pLon != null) points.push([pLat, pLon]);
  if (dLat != null && dLon != null) points.push([dLat, dLon]);
  const hasRoute = points.length === 2;

  useEffect(() => {
    if (!load?.id) return;
    setLoadingTravel(true);
    api.get(`/loads/${load.id}/travel-estimate`)
      .then((r) => setTravel(r.data))
      .catch(() => setTravel(null))
      .finally(() => setLoadingTravel(false));
  }, [load?.id]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="load-map-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-[720px] max-w-full h-full bg-white flex flex-col shadow-2xl">
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between bg-gray-900 text-white">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <RouteIcon className="w-4 h-4 text-orange-400" strokeWidth={2} />
              <div className="text-[10px] uppercase tracking-widest text-gray-400">Route</div>
            </div>
            <div className="font-display font-bold text-lg tracking-tight">{load?.bol_number}</div>
            <div className="text-[11px] font-mono text-gray-400 mt-0.5">
              <span
                className="inline-block px-1.5 py-0.5 mr-2 border"
                style={{ borderColor: STATUS_COLOR[load?.status] || "#9CA3AF",
                         color: STATUS_COLOR[load?.status] || "#9CA3AF" }}
              >{(load?.status || "").toUpperCase()}</span>
              {load?.soil_type} · {load?.weight_tons}t
            </div>
          </div>
          <button onClick={onClose} data-testid="load-map-close" className="text-gray-400 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Locate-me action bar */}
        <div className="px-5 py-2 border-b border-gray-200 bg-white flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-mono">
            {tracking ? (
              <span className="text-blue-700 font-bold flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
                Tracking · {breadcrumbs.length} pings
              </span>
            ) : myPos ? (
              myPos.near_dropoff
                ? <span className="text-emerald-700 font-bold">At dropoff · logged</span>
                : myPos.distance_mi != null
                  ? <>Logged · {myPos.distance_mi.toFixed(2)} mi from dropoff</>
                  : "Logged"
            ) : breadcrumbs.length > 0 ? (
              <>Breadcrumb trail · {breadcrumbs.length} pings</>
            ) : (
              "Tap Locate to log your position on this load"
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={locate}
              disabled={locating || tracking}
              data-testid="load-map-locate-me"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors"
            >
              {locating ? <Loader2 className="w-3 h-3 animate-spin" /> : <LocateFixed className="w-3 h-3" />}
              {locating ? "Locating…" : "Locate Me"}
            </button>
            {tracking ? (
              <button
                onClick={stopTracking}
                data-testid="load-map-stop-track"
                className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors"
              >
                <Square className="w-3 h-3" strokeWidth={2.5} /> Stop
              </button>
            ) : (
              <button
                onClick={startTracking}
                disabled={isTerminal}
                data-testid="load-map-start-track"
                title={isTerminal ? "Load is closed" : "Ping location every 60s"}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Radio className="w-3 h-3" /> Track Drive
              </button>
            )}
          </div>
        </div>

        {/* Route summary strip */}
        <div className="grid grid-cols-3 border-b border-gray-200 bg-gray-50 text-xs">
          <SummaryCell
            icon={<MapPin className="w-3.5 h-3.5 text-orange-500" />}
            label="Pickup"
            value={pickupSite.name || "\u2014"}
            sub={pickupSite.address}
          />
          <SummaryCell
            icon={<Flag className="w-3.5 h-3.5 text-emerald-600" />}
            label="Dropoff"
            value={dropoffSite.name || "\u2014"}
            sub={dropoffSite.address}
          />
          <SummaryCell
            icon={<Clock className="w-3.5 h-3.5 text-gray-500" />}
            label="Est. one-way"
            value={
              loadingTravel ? "…"
              : (travel?.available && travel?.distance_mi != null)
                ? `${travel.distance_mi.toFixed(1)} mi · ${Math.round(travel.one_way_minutes || 0)} min`
                : "—"
            }
            sub={(travel?.available && travel?.round_trip_minutes != null)
              ? `Round-trip ${Math.round(travel.round_trip_minutes)} min @ ${travel.avg_speed_mph || 40} mph`
              : "No coordinates"}
          />
        </div>

        {/* Map */}
        <div className="flex-1 bg-gray-100 relative" data-testid="load-map-canvas">
          {hasRoute ? (
            <MapContainer center={points[0]} zoom={12} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
              <TileLayer
                attribution="&copy; OpenStreetMap contributors"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds points={points} />
              <InvalidateSizeOnMount />
              <Marker position={points[0]} icon={PICKUP_ICON}>
                <Popup>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-gray-500">Pickup</div>
                  <div className="font-semibold">{pickupSite.name}</div>
                  <div className="text-xs text-gray-600">{pickupSite.address}</div>
                  {load?.picked_up_at && (
                    <div className="text-[10px] text-gray-500 mt-1 font-mono">Picked up {load.picked_up_at.replace("T", " ").slice(0, 16)}</div>
                  )}
                </Popup>
              </Marker>
              <Marker position={points[1]} icon={DROPOFF_ICON}>
                <Popup>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-gray-500">Dropoff</div>
                  <div className="font-semibold">{dropoffSite.name}</div>
                  <div className="text-xs text-gray-600">{dropoffSite.address}</div>
                  {load?.delivered_at && (
                    <div className="text-[10px] text-gray-500 mt-1 font-mono">Delivered {load.delivered_at.replace("T", " ").slice(0, 16)}</div>
                  )}
                </Popup>
              </Marker>
              <Polyline
                positions={points}
                pathOptions={{
                  color: STATUS_COLOR[load?.status] || "#9CA3AF",
                  weight: 4,
                  opacity: 0.85,
                  dashArray: load?.status === "delivered" ? null : "8 8",
                }}
              />
              {myPos && (
                <Marker position={[myPos.lat, myPos.lon]} icon={YOU_ARE_HERE_ICON}>
                  <Popup>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-blue-700">You are here</div>
                    <div className="text-xs text-gray-600">
                      {myPos.lat.toFixed(5)}, {myPos.lon.toFixed(5)}
                      {myPos.accuracy_m ? ` · ±${Math.round(myPos.accuracy_m)}m` : ""}
                    </div>
                    {myPos.distance_mi != null && (
                      <div className="text-[10px] text-gray-500 font-mono mt-1">
                        {myPos.near_dropoff
                          ? `At dropoff (${Math.round(myPos.distance_mi * 5280)} ft)`
                          : `${myPos.distance_mi.toFixed(2)} mi from dropoff`}
                      </div>
                    )}
                  </Popup>
                </Marker>
              )}
              {breadcrumbs.length >= 2 && (
                <Polyline
                  positions={breadcrumbs.map((b) => [b.lat, b.lon])}
                  pathOptions={{ color: "#2563EB", weight: 3, opacity: 0.9, dashArray: "2 6" }}
                />
              )}
              {breadcrumbs.map((b) => (
                <CircleMarker
                  key={b.id}
                  center={[b.lat, b.lon]}
                  radius={4}
                  pathOptions={{
                    color: "#fff", weight: 1.5,
                    fillColor: b.near_dropoff ? "#059669" : "#2563EB",
                    fillOpacity: 0.9,
                  }}
                >
                  <Popup>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-blue-700">Breadcrumb</div>
                    <div className="text-xs text-gray-600">{(b.at || "").replace("T", " ").slice(0, 19)}</div>
                    <div className="text-[10px] text-gray-500 font-mono mt-1">
                      {b.lat.toFixed(5)}, {b.lon.toFixed(5)}
                      {b.accuracy_m ? ` · ±${Math.round(b.accuracy_m)}m` : ""}
                    </div>
                    {b.distance_to_dropoff_mi != null && (
                      <div className="text-[10px] text-gray-500 font-mono">
                        {b.near_dropoff ? "At dropoff" : `${b.distance_to_dropoff_mi.toFixed(2)} mi from dropoff`}
                      </div>
                    )}
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          ) : (
            <div className="h-full flex items-center justify-center p-8">
              <div className="text-center max-w-xs">
                <Truck className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <div className="text-sm font-display font-bold mb-1">No coordinates yet</div>
                <div className="text-xs text-gray-500 font-mono leading-relaxed">
                  Add latitude / longitude to the pickup and dropoff sites (or on the load itself) to see the route.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCell({ icon, label, value, sub }) {
  return (
    <div className="p-3 border-r border-gray-200 last:border-r-0">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-gray-500 font-mono">
        {icon}<span>{label}</span>
      </div>
      <div className="text-sm font-semibold text-gray-900 mt-1 truncate">{value}</div>
      {sub && <div className="text-[10px] text-gray-500 font-mono truncate">{sub}</div>}
    </div>
  );
}
